import classnames from "classnames";
import metaverseLogo from "assets/images/digitalLogo.svg";
import spaceLogo from "assets/images/spaceLogoNew.svg";
import climateLogo from "assets/images/climateNewLogo.png";
import tqiNewLogo from "assets/images/tqiNewLogo.svg";
import burgerMenu from "assets/images/burgermenu.svg";
import { Link, useOutletContext } from "react-router-dom";
import { APP_ROUTES } from "const";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import {
  useAskAQuestionModal,
  useClearSearchField,
  useInitValues,
  useSearchBarListener,
  useSearchFieldSetter,
} from "./TopBar.hooks";
import React, { useEffect, useRef, useState } from "react";
import "./TopBar.styles.css";
import { useLogout } from "hooks/useLogout";
import { IOutletAuth, IProfile } from "types";
import config from "config";

import profileIcon from "assets/images/profile-icon.svg";
import profileIconHover from "assets/images/profile-icon-hover.svg";
import passIcon from "assets/images/pass-icon.svg";
import passIconHover from "assets/images/pass-icon-hover.svg";
import logoutIcon from "assets/images/logout-icon.svg";
import logoutIconHover from "assets/images/logout-icon-hover.svg";
import headphones from "assets/images/headphones.svg";
import Login from "assets/images/Login.svg";
import menuBook from "assets/images/menuBook.svg";

import classNames from "classnames";
import AskAQuestionModal from "./AskAQuestion/AskAQuestion.modal";
import { DarkModeToggle } from "components/DarkModeToggle";
import { SignedImg } from "components/SignedImg";

export interface IProps extends React.HTMLProps<HTMLDivElement> {
  showNavigation: boolean;
  toggleMenuHandler: () => void;
  userData: Partial<IProfile> | undefined;
}

function TopBar({
  className,
  showNavigation,
  userData,
  toggleMenuHandler,
  ...rest
}: IProps) {
  const classes = classnames(
    className,
    "flex w-full max-w-full fixed h-[56px] justify-between"
  );

  const ref = useRef<HTMLDivElement | null>(null);
  const refSearch = useRef<HTMLInputElement | null>(null);
  const refButton = useRef<HTMLDivElement | null>(null);

  const [searchInputValue, setSearchInputValue] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const logout = useLogout();

  const { isAskAQuestionModalShowing, toggleAskAQuestionModal } =
    useAskAQuestionModal();

  useOnClickOutside(ref, refButton, () => setModalOpen(false));
  useInitValues(setSearchInputValue);
  const debouncedSearch = useSearchFieldSetter();
  useSearchBarListener(); // on search query change
  useClearSearchField(setSearchInputValue);

  const authContext = useOutletContext<IOutletAuth>();

  const docLink =
    config.VERTICAL === "QUANTUM"
      ? "https://doc.clickup.com/26485008/d/h/t888g-10672/978c374e75e2e11"
      : config.VERTICAL === "METAVERSE"
      ? "https://doc.clickup.com/26485008/d/h/t888g-5112/194681f6e5e4b18"
      : config.VERTICAL === "SPACE"
      ? "https://doc.clickup.com/26485008/d/h/t888g-7272/6d6595644e9e5f0"
      : config.VERTICAL === "CLIMATE"
      ? "https://doc.clickup.com/26485008/d/h/t888g-8592/99143dea9437d67"
      : "";

  const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        refSearch?.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <header
      {...rest}
      className={classes}
      style={{ backgroundColor: config.BACKGROUND_COLOR }}
    >
      <div
        className={classnames(
          "pointer cursor-pointer  inline-flex max-w-[208px]  items-center ml-5 mx-3 "
        )}
      >
        <div>
          <img
            className="z-10 min-w-[24px] w-full h-full"
            src={burgerMenu}
            alt="menu"
            onClick={toggleMenuHandler}
          />
        </div>
      </div>

      <div className="flex items-center w-full justify-between transition-all ">
        <div className="flex flex-row items-center">
          <div
            className={classnames("hidden sm:inline min-w-[36px] md:mx-[20px]")}
          >
            {process.env.REACT_APP_PLATFORM === "QUANTUM" ? (
              <img src={tqiNewLogo} alt="logo" className="h-[36px]" />
            ) : process.env.REACT_APP_PLATFORM === "METAVERSE" ? (
              <img src={metaverseLogo} alt="logo" className="h-[36px]" />
            ) : process.env.REACT_APP_PLATFORM === "SPACE" ? (
              <img src={spaceLogo} alt="logo" className="h-[36px]" />
            ) : process.env.REACT_APP_PLATFORM === "CLIMATE" ? (
              <img src={climateLogo} alt="logo" className="h-[36px]" />
            ) : (
              <></>
            )}
          </div>
          <div className="relative">
            <div className="absolute right-[10px] text-[12px] text-[#98A0A6] flex h-full items-center">
              {isMac ? "⌘K" : "Ctrl+K"}
            </div>
            <input
              ref={refSearch}
              className="ml-4 w-[120px] lg:w-[404px] md:w-[303px] sm:w-[252px] h-[38px] p-3 pl-5 rounded text-[12px] font-normal text-[#98A0A6] bg-primary"
              placeholder="Search"
              value={searchInputValue}
              onChange={(e) => {
                setSearchInputValue(e.target.value);
                debouncedSearch(e.target.value);
              }}
              id="Search"
            />
          </div>
        </div>

        <div className="flex">
          <DarkModeToggle />
          <div
            onClick={toggleAskAQuestionModal}
            className="flex mx-2 select-none content-center items-center justify-center cursor-pointer"
          >
            <img
              src={headphones}
              alt={`Ask a ${config.PLATFORM_NAME} Expert`}
              className={classNames("h-20px", {})}
            />
            <div className="hidden md:flex ml-2 text-[#FFFFFF] text-[14px] font-bold">
              Ask a {config.PLATFORM_NAME} Expert
            </div>
          </div>

          {!!docLink ? (
            <a
              href={docLink}
              target="_blank"
              rel="noreferrer"
              className="flex mr-2 ml-4 select-none content-center items-center justify-center cursor-pointer"
            >
              <img src={menuBook} alt="Documentation" width={20} />
            </a>
          ) : (
            <></>
          )}

          {/* {!isPremium && (
            <PremiumButton className="hidden md:flex ml-4 hover:bg-[#28C18A]" />
          )} */}

          {authContext.isAuth ? (
            <div
              ref={refButton}
              className="flex min-w-[38px] width-[38px] mx-4"
            >
              <button onClick={() => setModalOpen(!isModalOpen)}>
                {(userData?.profilePic || "") !== "" ? (
                  <SignedImg
                    alt="profile"
                    className="max-w-[38px] min-h-[38px] max-h-[38px] rounded-full"
                    src={userData?.profilePic!}
                  />
                ) : (
                  <div className="h-[38px] w-[38px] select-none min-w-[38px] bg-tableSecondary text-primaryBlue border border-primary text-[14px] rounded-full flex content-center items-center justify-center">
                    {userData?.firstName && userData?.lastName
                      ? userData?.firstName?.replaceAll('"', "").charAt(0) +
                        userData?.lastName?.replaceAll('"', "").charAt(0)
                      : userData?.email?.charAt(0)}
                  </div>
                )}
              </button>
            </div>
          ) : (
            <Link
              className="h-[36px] flex ml-2 md:ml-4 mx-4 select-none content-center items-center justify-center cursor-pointer text-[#FFFFFF] hover:opacity-80 text-[12px] font-bold border border-white rounded px-4"
              to={APP_ROUTES.LOGIN}
            >
              <img src={Login} alt="Login" className="hidden md:flex mr-2" />
              Login
            </Link>
          )}
        </div>
      </div>
      {isModalOpen && authContext.isAuth ? (
        <>
          <div
            className="absolute top-16 right-4 text-primary bg-primary w-[330px] h-[186px] z-40 flex flex-col shadow-companyProfileLogo rounded-sm"
            ref={ref}
          >
            <div className="w-full h-full">
              <div className="w-full flex flex-row p-4 mb-1 border-b border-primary">
                {(userData?.profilePic || "") !== "" ? (
                  <SignedImg
                    alt="profile"
                    className="flex-shrink-0 max-w-[38px] min-h-[38px] max-h-[38px] mr-[12px] rounded-full"
                    src={userData?.profilePic!}
                  />
                ) : (
                  <div className="flex-shrink-0 w-[38px] h-[38px] select-none mr-[12px] bg-tableSecondary text-primaryBlue text-[14px] rounded-full flex content-center items-center justify-center">
                    {userData?.firstName && userData?.lastName
                      ? userData?.firstName?.replaceAll('"', "").charAt(0) +
                        userData?.lastName?.replaceAll('"', "").charAt(0)
                      : userData?.email?.charAt(0)}
                  </div>
                )}

                <div className="break-words mt-[6px]">
                  <p className="break-words break-all font-medium text-[16px] text-primary">
                    {userData?.email}
                  </p>
                </div>
              </div>
              <div className="w-full flex flex-col text-[12px] mt-2">
                <Link
                  className={classnames(
                    "inline-block mt-1 mb-1 text-[14px] font-bold text-primary hover:text-primaryBlue px-[18px]"
                  )}
                  to={APP_ROUTES.PROFILE}
                  onClick={() => {
                    setModalOpen(false);
                  }}
                  onMouseEnter={(e) =>
                    // @ts-ignore
                    (e.currentTarget.querySelector("img").src =
                      profileIconHover)
                  }
                  onMouseLeave={(e) =>
                    //@ts-ignore
                    (e.currentTarget.querySelector("img").src = profileIcon)
                  }
                >
                  <img
                    className="inline-block mr-[6px] mt-[-2px] w-4"
                    src={profileIcon}
                    alt="profileIcon"
                  />{" "}
                  Profile
                </Link>
                <Link
                  className="mt-2 text-[14px] mb-1 font-bold text-primary hover:text-primaryBlue px-[18px]"
                  to={APP_ROUTES.CHANGE_PASSWORD}
                  onClick={() => {
                    setModalOpen(false);
                  }}
                  onMouseEnter={(e) =>
                    // @ts-ignore
                    (e.currentTarget.querySelector("img").src = passIconHover)
                  }
                  onMouseLeave={(e) =>
                    //@ts-ignore
                    (e.currentTarget.querySelector("img").src = passIcon)
                  }
                >
                  <img
                    className="inline-block mr-[6px] mt-[-2px] w-4"
                    src={passIcon}
                    alt="passIcon"
                  />{" "}
                  Change password
                </Link>
                <button
                  className="mt-2 text-[14px] font-bold text-primary hover:text-primaryBlue text-left px-[18px]"
                  onClick={logout}
                  onMouseEnter={(e) =>
                    // @ts-ignore
                    (e.currentTarget.querySelector("img").src = logoutIconHover)
                  }
                  onMouseLeave={(e) =>
                    //@ts-ignore
                    (e.currentTarget.querySelector("img").src = logoutIcon)
                  }
                >
                  <img
                    className="inline-block mr-[6px] mt-[-2px] w-4"
                    src={logoutIcon}
                    alt="logoutIcon"
                  />{" "}
                  Logout
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <AskAQuestionModal
        isShowing={isAskAQuestionModalShowing}
        onHide={toggleAskAQuestionModal}
      />
    </header>
  );
}

export default TopBar;
